/**
 * /well-testing/wells — the tenant's wells register
 * (GET /well-testing/wells, fieldiq-api#1241, WT-1).
 *
 * Full-width register; a row opens the well on its own page. Filters live in
 * the URL (fluid type, customer, search) so the back arrow returns to the list
 * as it was left. Every filter is server-side.
 *
 * IDENTIFIER BY COUNTRY: a US tenant's wells carry an API number, every other
 * tenant's a UWI (`meta.country`). The column shows that one field as typed;
 * absent is `—`.
 *
 * The counts are a tally of the unfiltered, unpaginated list (no endpoint
 * returns one). The customer filter's options come from that same list, so any
 * role that can read wells gets them.
 *
 * New Well is WT_WELL_WRITE (admin, office_staff, supervisor) — lib/well-testing.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Droplet, Flame, MapPin, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWtMeta, useWtWells } from '../../hooks/useWellTesting';
import { queryLoadFailure } from '../../lib/load-failure';
import { canWriteWells, catalogLabel, wellIdLabel, wellIdOf, withUnit } from '../../lib/well-testing';
import type { WtWell } from '../../types/well-testing';
import { useOpenRecord } from '../downhole/recordPage';
import { WT_EYEBROW, WT_INPUT, WT_RAIL, WT_SOUR_SKIN, WtBadge, WtChip, WtChipRow, WtEmpty, WtPanel, WtRegister, type WtColumn } from './WtBits';

export default function WtWellsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const fluidType = searchParams.get('fluidType') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const qParam = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(qParam);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== qParam) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const meta = useWtMeta();
  const catalog = meta.data?.data ?? null;
  const filters = useMemo(() => ({ fluidType, customerId, q: qParam }), [fluidType, customerId, qParam]);
  const filtered = !!(fluidType || customerId || qParam);
  const listQuery = useWtWells(filters);
  const allQuery = useWtWells();
  const wells = listQuery.data?.data ?? [];
  const tenant = listQuery.data?.meta ?? allQuery.data?.meta ?? meta.data?.meta ?? null;
  const idLabel = wellIdLabel(tenant);
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (w: WtWell) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canWriteWells(user?.role);

  const customers = useMemo(() => {
    const seen = new Map<string, string>();
    (all ?? []).forEach((w) => seen.set(w.customerId, w.customerName ?? '—'));
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const fluid = (w: WtWell) => w.fluidTypeLabel ?? catalogLabel(catalog?.fluidTypes, w.fluidType);

  const columns: WtColumn<WtWell>[] = [
    {
      key: 'name',
      header: 'Well',
      render: (w) => (
        <>
          <p className="font-extrabold text-white">{w.wellName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{w.customerName || '—'}</p>
        </>
      ),
    },
    { key: 'identifier', header: idLabel, render: (w) => <span className="font-mono text-sm">{wellIdOf(w, tenant) || '—'}</span> },
    { key: 'licence', header: 'Licence', render: (w) => <span className="font-mono text-sm">{w.licenceNumber || '—'}</span> },
    { key: 'location', header: 'Location', render: (w) => w.surfaceLocation || '—' },
    { key: 'province', header: 'Prov / state', render: (w) => w.provinceState || '—' },
    { key: 'fluid', header: 'Fluid', render: fluid },
    {
      key: 'sour',
      header: 'Sour / H2S',
      render: (w) =>
        w.sour ? (
          <span className="inline-flex items-center gap-2 whitespace-nowrap">
            <WtBadge skin={WT_SOUR_SKIN} value="sour" />
            <span className="font-mono text-sm">{withUnit(w.expectedH2sPpm, 'ppm')}</span>
          </span>
        ) : (
          <span className="whitespace-nowrap">
            Sweet
            {w.expectedH2sPpm != null && <span className="ml-2 font-mono text-sm">{withUnit(w.expectedH2sPpm, 'ppm')}</span>}
          </span>
        ),
    },
  ];

  return (
    <div data-testid="wt-wells-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title="Wells"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => openRecord('/well-testing/wells/new')} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New well
            </button>
          ) : undefined
        }
        stats={[
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Wells', sub: 'Registered', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <Droplet className="size-4" aria-hidden="true" />, label: 'Oil', tone: 'neutral', loading: countsLoading, value: countOf((w) => w.fluidType === 'oil') },
          { icon: <Flame className="size-4" aria-hidden="true" />, label: 'Gas', tone: 'neutral', loading: countsLoading, value: countOf((w) => w.fluidType === 'gas') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Sour', sub: 'H2S service', tone: 'accent', loading: countsLoading, value: countOf((w) => w.sour) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WtChipRow label="Filter by fluid">
            <WtChip label="All" active={!fluidType} onClick={() => setParam('fluidType', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {(catalog?.fluidTypes ?? []).map((f) => (
              <WtChip
                key={f.value}
                label={f.label}
                active={fluidType === f.value}
                onClick={() => setParam('fluidType', f.value)}
                count={countsLoading ? undefined : countOf((w) => w.fluidType === f.value)}
              />
            ))}
          </WtChipRow>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
            <label className="relative block">
              <span className="sr-only">Search well, {idLabel}, licence, location or customer</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search well, ${idLabel}, licence, location or customer`}
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select className={WT_INPUT} aria-label="Filter by customer" value={customerId} onChange={(e) => setParam('customerId', e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <WtPanel
            eyebrow="Well register"
            title="Wells"
            testId="wt-well-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && wells.length > 0
                ? filtered
                  ? `Showing ${wells.length} of ${countOf(() => true)} wells`
                  : `${wells.length} well${wells.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <WtRegister
              query={listQuery}
              rows={wells}
              columns={columns}
              rail={() => WT_RAIL}
              onOpen={(w) => openRecord(`/well-testing/wells/${w.id}`)}
              subject="wells"
              testId="wt-well"
              // Sized to FIT the register panel at 1280 (about 970px wide beside
              // the sidebar). The table's overflow-x scroll has no visible bar on
              // macOS, so a minimum wider than the panel read as clipping: the
              // last column cut to "18,5" and the TX header to "SOUR / H2".
              minWidth="min-w-[860px]"
              empty={filtered ? <WtEmpty>No well matches these filters.</WtEmpty> : <WtEmpty>There are no wells yet.</WtEmpty>}
              card={(w) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{w.wellName || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {w.customerName || '—'} · {fluid(w)}
                      </p>
                    </div>
                    {w.sour && <WtBadge skin={WT_SOUR_SKIN} value="sour" />}
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">
                    {idLabel} {wellIdOf(w, tenant) || '—'} · {w.surfaceLocation || '—'} · {w.provinceState || '—'}
                  </p>
                </>
              )}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
